import Axios from "../axios";
import {virtualAnnouncementApiConstants} from "../constants/ApiConstant";

// Backend integration for the Virtual Announcement feature.
//
// localStorage remains the source of truth the scheduler reads synchronously on
// every tick; this service mirrors those announcements to the backend for the
// logged-in school so they survive a browser wipe and follow the school across
// devices. Every write is fire-and-forget: it can NEVER block or break the UI /
// playback. Failed writes are parked in a localStorage queue (keyed by id, last
// write wins) and flushed as a single batch on the next successful call.
//
// The school is derived on the backend from the auth token, so no schoolId is
// ever sent. `days` travels as weekday codes (["MON","TUE"]) on the wire but is
// kept as numbers (0=Sun … 6=Sat) locally to match the scheduler / UI.

const DAY_NUM_TO_CODE = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const DAY_CODE_TO_NUM = DAY_NUM_TO_CODE.reduce((acc, code, index) => {
    acc[code] = index;
    return acc;
}, {});

const OFFLINE_QUEUE_KEY = "virtualAnnouncement:sync-queue";

/* ----------------------------- shape mapping ----------------------------- */

const daysToCodes = (days) => {
    if (!Array.isArray(days)) {
        return [];
    }
    return days
        .map((day) => (typeof day === "number" ? DAY_NUM_TO_CODE[day] : String(day).toUpperCase()))
        .filter((code) => DAY_CODE_TO_NUM[code] !== undefined);
};

const codesToDays = (days) => {
    if (!Array.isArray(days)) {
        return [];
    }
    return days
        .map((day) => (typeof day === "number" ? day : DAY_CODE_TO_NUM[String(day).toUpperCase()]))
        .filter((num) => typeof num === "number" && num >= 0 && num <= 6);
};

// Local schedule object -> API wire object.
export const mapScheduleToWire = (schedule = {}) => ({
    id: schedule.id,
    title: schedule.title || "",
    type: schedule.type || "text",
    text: schedule.text || null,
    // Uploaded audio (voice recording / music) is saved with its server file
    // URL so it can be played back on any device, not just where it was made.
    url: schedule.url || null,
    recordingId: schedule.recordingId || null,
    recordingName: schedule.recordingName || null,
    openingSound: schedule.openingSound || null,
    rate: typeof schedule.rate === "number" ? schedule.rate : null,
    pitch: typeof schedule.pitch === "number" ? schedule.pitch : null,
    voiceName: schedule.voiceName || null,
    lang: schedule.lang || null,
    gender: schedule.gender || null,
    time: schedule.time || null,
    days: daysToCodes(schedule.days),
    repeat: schedule.repeat !== false,
    status: schedule.status === "N" ? "N" : "Y",
    createdAt: schedule.createdAt || Date.now(),
});

// API wire object -> local schedule object (defaults mirror ScheduleModal).
export const mapWireToSchedule = (wire = {}) => ({
    id: wire.id,
    title: wire.title || "",
    type: wire.type || "text",
    text: wire.text || "",
    url: wire.url || "",
    recordingId: wire.recordingId || "",
    recordingName: wire.recordingName || "",
    openingSound: wire.openingSound || "chime",
    rate: typeof wire.rate === "number" ? wire.rate : 0.95,
    pitch: typeof wire.pitch === "number" ? wire.pitch : 1,
    voiceName: wire.voiceName || "",
    lang: wire.lang || "",
    gender: wire.gender || "",
    time: wire.time || "",
    days: codesToDays(wire.days),
    repeat: wire.repeat !== false,
    status: wire.status === "N" ? "N" : "Y",
    createdAt: wire.createdAt || Date.now(),
});

/* ------------------------------ raw network ------------------------------ */

// Batch upsert. Accepts one schedule or an array of local schedules; always
// POSTs a JSON array as the backend expects. Rejects on network/server error.
export async function saveVirtualAnnouncements(schedules = []) {
    const list = Array.isArray(schedules) ? schedules : [schedules];
    const payload = list.map(mapScheduleToWire);
    return Axios.post(virtualAnnouncementApiConstants.SAVE_VIRTUAL_ANNOUNCEMENTS, payload);
}

// List the logged-in school's announcements, mapped back to local shape.
export async function listVirtualAnnouncements() {
    const response = await Axios.get(virtualAnnouncementApiConstants.LIST_VIRTUAL_ANNOUNCEMENTS);
    const data = Array.isArray(response?.data) ? response.data : [];
    return data.map(mapWireToSchedule);
}

/* ---------------------------- offline queue ------------------------------ */

const readQueue = () => {
    try {
        const raw = localStorage.getItem(OFFLINE_QUEUE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
        return {};
    }
};

const writeQueue = (queue) => {
    try {
        localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
    } catch (e) {
        // localStorage full / unavailable — the write is only a best-effort
        // mirror of data already persisted locally, so we drop it silently.
    }
};

const enqueue = (wire) => {
    if (!wire?.id) {
        return;
    }
    const queue = readQueue();
    queue[wire.id] = wire;
    writeQueue(queue);
};

// Flush anything parked from earlier failures as one batch. Resolves true when
// the queue was flushed (or already empty), false when it was kept for a retry.
// Never throws.
export async function flushVirtualAnnouncementQueue() {
    const queue = readQueue();
    const items = Object.values(queue);

    if (items.length === 0) {
        return true;
    }

    try {
        await Axios.post(virtualAnnouncementApiConstants.SAVE_VIRTUAL_ANNOUNCEMENTS, items);
        writeQueue({});
        return true;
    } catch (e) {
        return false;
    }
}

// Fire-and-forget upsert of a single schedule. Safe to call without awaiting —
// it always resolves and never rejects, so it cannot affect the UI or playback.
// A soft-delete is just an upsert with status "N".
export async function queueVirtualAnnouncementUpsert(schedule) {
    if (!schedule?.id) {
        return;
    }
    const wire = mapScheduleToWire(schedule);

    // Opportunistically drain earlier failures first so a recovered connection
    // catches up before the fresh write goes out.
    await flushVirtualAnnouncementQueue();

    try {
        await Axios.post(virtualAnnouncementApiConstants.SAVE_VIRTUAL_ANNOUNCEMENTS, [wire]);
    } catch (e) {
        enqueue(wire);
    }
}
